import React from 'react';
import {
    Button,
    Flex,
    HTMLDialog,
    Icon,
    InputProperties,
    PriorityIcon,
    Separator,
    Text,
    Title
} from '@priority-software/priority-style-react';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {mapListDataByType, OptionType} from './utils';
import {SearchableList} from '../../../../components/SearchableList';
import {ActionIcon} from '../../../../components/ActionIcon';
import {useDialogContent} from './hooks/useDialogContent';
import {DIALOG_ID, INPUT_PORTAL_ID} from './constants';
import {listTypes} from './enum';
import texts from '../../../../components/Common/SelectListModal/text';
import {ReactComponent as NoFormsSVG} from './assets/empty-form-search.svg';
import {ReactComponent as NoActionssSVG} from './assets/empty-actions-search.svg';
import styles from './style.module.scss';

type Props = {
    onDialogClose: () => void;
    runInSearchMode: boolean;
    type: listTypes | null;
};

const SEARCH_INPUT_PROPS = {
    roundedCorners: false,
    size: 'large',
    componentClasses: styles.searchInput,
    children: <Icon icon={PriorityIcon.searchThin} />,
    autoFocus: true
} as InputProperties;

export const FormActionDialog = (props: Props) => {
    const {onDialogClose, runInSearchMode, type} = props;

    const {
        onAccept,
        onFocusChange,
        onItemClick,
        selectedValue,
        handleShowHelpMessage,
        listRef,
        getNoOptionsText
    } = useDialogContent({onDialogClose, type});

    const items = mapListDataByType(type, runInSearchMode);

    const content = (
        <div className={styles.listWrap} ref={listRef}>
            <SearchableList
                key={`${type}`}
                data={items}
                onFocusChange={onFocusChange}
                onEnter={onAccept}
                autoHeightMax={240}
                selectedValue={selectedValue}
                onDoubleClick={onAccept}
                itemRenderer={
                    type !== listTypes.FormSons
                        ? item => {
                              return (
                                  <Flex justifyContent="flex-start" style={{gap: 8}}>
                                      <ActionIcon
                                          rounded
                                          type={(item as OptionType)?.data?.type as number}
                                      />
                                      <Text skin="paragraph-2">{item.label}</Text>
                                  </Flex>
                              );
                          }
                        : undefined
                }
                onItemClick={onItemClick}
                inputProps={SEARCH_INPUT_PROPS}
                inputPortalId={INPUT_PORTAL_ID}
                noOptionsText={
                    <Flex
                        direction={'column'}
                        alignItems={'center'}
                        justifyContent="center"
                        style={{flexGrow: 1, paddingTop: 32}}
                    >
                        {type === listTypes.FormSons ? (
                            <NoFormsSVG width={168} height={151} />
                        ) : (
                            <NoActionssSVG width={199} height={136} />
                        )}
                        <div>{getNoOptionsText()}</div>
                    </Flex>
                }
            />
        </div>
    );

    return (
        <HTMLDialog
            isOpen
            id={DIALOG_ID}
            draggable
            componentClasses={styles.dialog}
            content={content}
            onClose={onDialogClose}
            title={<div className={styles.searchBlock} id={INPUT_PORTAL_ID} />}
            toolbarContent={
                <Flex>
                    <Title
                        skin="h4"
                        text={texts[type]}
                        textColor="primary-700"
                        style={{lineHeight: 1}}
                    />
                    <Flex justifyContent={'flex-end'} componentClasses={styles.dialogInfo}>
                        <ModernTooltip
                            tooltip={texts.help}
                            maxWidth={400}
                            arrowOffset={8}
                            position="left-start"
                            tooltipOffset={8}
                            portalId={DIALOG_ID}
                        >
                            <Icon
                                onClick={handleShowHelpMessage}
                                icon={PriorityIcon.questionCircle}
                                iconColor="secondary-500"
                                size="small"
                            />
                        </ModernTooltip>
                        <Separator
                            align="horizontal"
                            height={25}
                            style={{backgroundColor: 'var(--secondary-color-400)'}}
                        />
                    </Flex>
                </Flex>
            }
            footer={
                <Flex justifyContent="flex-end" componentClasses={styles.footerContent}>
                    <Button onClick={onAccept} size="medium" skin="primary" wideButton>
                        {texts.ok}
                    </Button>
                    <Button
                        onClick={onDialogClose}
                        name="cancel"
                        size="medium"
                        skin="secondary"
                        wideButton
                    >
                        {texts.cancel}
                    </Button>
                </Flex>
            }
            width={520}
            size="small"
        />
    );
};
